import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, Globe, MapPin, PenLine, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CompanyLogo, Skeleton, UserAvatar } from "@/components/common";
import { INDUSTRY_LABEL } from "@/pages/CustomersPage";
import { compactMoney, ago } from "@/lib/sk";
import { CustomerTimeline } from "@/components/customer/CustomerTimeline";
import { Section, Projects, Quotes, Contacts, Fleet, Systems, Notes, Facts } from "@/components/customer/CustomerSections";

function Kpi({ label, value, sub, tone, testId }) {
  return (
    <div className="min-w-0 px-5 py-4 first:pl-6 last:pr-6" data-testid={testId}>
      <div className="truncate text-[12px] font-medium text-muted-foreground">{label}</div>
      <div className={cn("mt-1 truncate font-display text-[22px] font-bold leading-none tracking-[-0.02em] tabular-nums", tone || "text-foreground")}>{value}</div>
      <div className="mt-1.5 truncate font-mono text-[11.5px] tabular-nums text-muted-foreground">{sub || " "}</div>
    </div>
  );
}

/** Kunde 360 - everything Skarpenord knows about one customer, on one calm page. */
export default function CustomerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const shell = useOutletContext() || {};
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/accounts/${id}/overview`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { setD(null); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startWork = async () => {
    setBusy(true);
    try {
      const r = await api.post("/projects", { text: "", customer_name: d.account.name });
      window.dispatchEvent(new Event("projects:changed"));
      navigate(`/p/${r.data.project.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  if (error) return <div className="p-10 text-center text-[13.5px] text-muted-foreground" data-testid="customer-error">{error} · <Link to="/customers" className="text-foreground underline">Til kunder</Link></div>;
  if (!d) return <div className="mx-auto max-w-[1100px] space-y-4 px-8 py-8"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-24" /><Skeleton className="h-48" /></div>;
  const a = d.account;
  const s = d.stats;
  const first = a.name.split(" ")[0];

  return (
    <div className="mx-auto w-full max-w-[1100px] px-5 pb-20 pt-5 sm:px-8" data-testid="customer-page">
      <Link to="/customers" className="inline-flex h-8 items-center gap-1.5 rounded-full pr-2 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground" data-testid="customer-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Kunder</Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-5 fade-up">
        <div className="flex min-w-0 items-start gap-4">
          <CompanyLogo name={a.name} domain={a.website} size="xl" className="h-16 w-16 rounded-[16px] p-2.5" />
          <div className="min-w-0">
            <h1 className="font-display text-[30px] font-bold leading-[1.05] tracking-[-0.03em] text-[hsl(var(--navy))] sm:text-[34px]" data-testid="customer-name">{a.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
              <span className="font-medium text-foreground/80">{INDUSTRY_LABEL[a.industry] || a.industry}</span>
              {a.city || a.country ? <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[a.city, a.country].filter(Boolean).join(", ")}</span> : null}
              {a.website ? <a href={`https://${a.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground" data-testid="customer-website-link"><Globe className="h-3.5 w-3.5" />{a.website.replace(/^https?:\/\//, "")}</a> : null}
              {a.owner?.name ? <span className="inline-flex items-center gap-1.5" data-testid="customer-owner"><UserAvatar name={a.owner.name} initials={a.owner.initials} color={a.owner.color} src={a.owner.avatar_url} size="xs" /> {a.owner.name} eier kunden</span> : null}
            </div>
            {a.description ? <p className="mt-3 max-w-[640px] text-[14px] leading-relaxed text-foreground/80">{a.description}</p> : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {shell.toggleAssistant ? <button type="button" onClick={shell.toggleAssistant} className="inline-flex h-10 items-center gap-2 rounded-full border border-black/[0.08] bg-white px-4 text-[13px] font-medium text-foreground/80 transition-colors hover:border-black/[0.18] hover:text-foreground" data-testid="customer-ask-assistant-button"><Sparkles className="h-4 w-4 text-primary" /> Spør om {first}</button> : null}
          <button type="button" disabled={busy} onClick={startWork} className="inline-flex h-10 items-center gap-2 rounded-full bg-[hsl(var(--brand))] px-4 text-[13px] font-semibold text-white transition-[background-color,transform] hover:bg-[hsl(var(--brand-strong))] active:scale-[0.98] disabled:opacity-40" data-testid="customer-new-project-button"><PenLine className="h-4 w-4" /> Nytt arbeid for {first}</button>
        </div>
      </header>

      <div className="mt-7 grid grid-cols-2 divide-y divide-black/[0.05] overflow-hidden rounded-[18px] border border-black/[0.06] bg-white sm:grid-cols-3 sm:divide-y-0 lg:grid-cols-6 [&>*:not(:first-child)]:sm:border-l [&>*]:sm:border-black/[0.05] fade-up d-1" data-testid="customer-stats">
        <Kpi label="Åpne saker" value={s.open_count} sub={s.open_value ? compactMoney(s.open_value) : "–"} testId="customer-stat-open" />
        <Kpi label="Tilbud ute hos kunden" value={s.quotes_out} sub={s.quotes_out_value ? compactMoney(s.quotes_out_value) : "–"} tone={s.quotes_out ? "text-primary" : undefined} testId="customer-stat-quotes" />
        <Kpi label="Vunnet" value={s.won_count} sub={s.won_value ? compactMoney(s.won_value) : "–"} testId="customer-stat-won" />
        <Kpi label="Treffrate" value={s.hit_rate != null ? `${s.hit_rate} %` : "–"} sub={s.won_count + s.lost_count ? `${s.won_count} av ${s.won_count + s.lost_count} avgjorte` : "ingen avgjorte ennå"} testId="customer-stat-hit" />
        <Kpi label="Ventiler" value={s.valves} sub={s.avg_gm_pct != null ? `snitt DB ${s.avg_gm_pct} %` : "–"} testId="customer-stat-valves" />
        <Kpi label="Siste aktivitet" value={s.last_touch ? ago(s.last_touch).replace(" siden", "") : "–"} sub={s.last_touch ? "siden" : "–"} testId="customer-stat-touch" />
      </div>

      <div className="mt-10 grid gap-x-12 gap-y-10 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-10">
          <Section title="Tidslinje" count={d.timeline.length} testId="customer-timeline-section">
            <div className="pt-4"><CustomerTimeline events={d.timeline} /></div>
          </Section>
          <Projects projects={d.projects} />
          <Quotes projects={d.projects} />
        </div>
        <aside className="min-w-0 space-y-10">
          <Contacts accountId={id} contacts={d.contacts} onChanged={load} />
          <Fleet vessels={d.vessels} />
          <Systems systems={d.systems} types={d.types} total={s.valves} />
          <Notes accountId={id} value={a.notes} />
          <Facts account={a} stats={s} />
        </aside>
      </div>
    </div>
  );
}
